import React, { useEffect, useRef } from 'react'
import { isNil } from 'ramda'
import { createSelector, createStructuredSelector } from 'reselect'
import { compose, withState, withProps, withHandlers } from 'recompose'
import { cn } from 'design-system'

import * as inputSelectors from '../selectors'
import InputWrapper from '../InputWrapper'

import styles from './index.module.scss'

const ToggleSwitch = ({
  label,
  labelSibling,
  value,
  finalChecked: checked,
  disabled = false,
  helpText,
  infoText,
  describedBy,
  finalId,
  finalLabelId,
  errorMessageId,
  helpTextId,
  infoTextId,
  isFocused,
  handleChange,
  handleFocus,
  handleBlur,
  handleLabelClick,
  errorMessage,
  wrapperClass,
  inline = false,
  checkbox,
  setInputRef
}) => {
  const inputRef = useRef()

  useEffect(() => {
    setInputRef(inputRef.current)
  }, [setInputRef])

  return (
    <InputWrapper
      label={!inline && label}
      labelSibling={labelSibling}
      labelId={!inline ? finalLabelId : ''}
      inputId={finalId}
      errorMessage={errorMessage}
      errorMessageId={errorMessageId}
      helpText={helpText}
      helpTextId={helpTextId}
      infoText={infoText}
      infoTextId={infoTextId}
      focused={isFocused}
      className={wrapperClass}>
      <div
        className="inline-flex items-center"
        role={inline ? 'group' : undefined}
        aria-label={inline && label ? label : undefined}>
        <>
          <input
            id={finalId}
            type="checkbox"
            role={checkbox ? undefined : 'switch'}
            className="peer sr-only"
            {...(value && { value })}
            checked={!!checked}
            disabled={!!disabled}
            aria-invalid={!!errorMessage}
            aria-labelledby={finalLabelId}
            {...(describedBy && { 'aria-describedby': describedBy })}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={inputRef}
          />
          {checkbox ? (
            <span
              role="presentation"
              className={cn(
                styles.checkbox,
                'db relative rounded border border-solid peer-focus-visible:ring-2 peer-focus-visible:ring-yd-blue peer-focus-visible:ring-offset-2',
                {
                  'border-yd-blue bg-yd-blue': !disabled && checked,
                  'border-neutral-600 bg-white': !disabled && !checked,
                  'cursor-pointer': !disabled,
                  'bg-neutral-400': disabled,
                  'ring-2 ring-yd-blue ring-offset-2': isFocused && !disabled
                }
              )}
              onClick={handleLabelClick}>
              <span
                className={cn('h-full w-full', styles.checkmark, {
                  [styles['checkmark--checked']]: checked
                })}
              />
            </span>
          ) : (
            <span
              role="presentation"
              className={cn(
                styles.switch,
                'relative block h-8 w-16 rounded-full peer-focus-visible:ring-2 peer-focus-visible:ring-yd-blue peer-focus-visible:ring-offset-2',
                {
                  'bg-yd-blue': !disabled && checked,
                  'bg-neutral-100': !disabled && !checked,
                  'cursor-pointer': !disabled,
                  'bg-neutral-400': disabled,
                  'ring-2 ring-yd-blue ring-offset-2': isFocused && !disabled
                }
              )}
              onClick={handleLabelClick}>
              <span
                className={cn(
                  'absolute left-0 top-0 inline-block w-4 rounded-full bg-white shadow-3',
                  styles.indicator,
                  {
                    [styles['indicator--checked']]: checked
                  }
                )}
              />
            </span>
          )}
        </>
        {inline && label && (
          <label
            id={finalLabelId}
            htmlFor={finalId}
            className="ml-2 cursor-pointer">
            {label}
          </label>
        )}
      </div>
    </InputWrapper>
  )
}

const finalCheckedSelector = createSelector(
  props => props.checked,
  props => props.checkedInternal,
  (checked, checkedInternal) => (!isNil(checked) ? checked : checkedInternal)
)

const propSelectors = createStructuredSelector({
  finalChecked: finalCheckedSelector,
  finalId: inputSelectors.idSelector,
  finalLabelId: inputSelectors.labelIdSelector,
  errorMessageId: inputSelectors.errorMessageIdSelector,
  helpTextId: inputSelectors.helpTextIdSelector,
  infoTextId: inputSelectors.infoTextIdSelector,
  describedBy: inputSelectors.describedBySelector
})

export default compose(
  withState('inputRef', 'setInputRef', React.createRef()),
  withState('isFocused', 'setFocused', false),
  withState('checkedInternal', 'setCheckedInternal', finalCheckedSelector),
  withProps(propSelectors),
  withHandlers({
    handleChange:
      ({ onChange, onToggle, setCheckedInternal }) =>
      event => {
        setCheckedInternal(event.target.checked)

        if (onChange) {
          onChange(event)
        }
        if (onToggle) {
          onToggle(event.target.checked)
        }
      },
    handleFocus:
      ({ setFocused }) =>
      () =>
        setFocused(true),
    handleBlur:
      ({ setFocused }) =>
      () =>
        setFocused(false),
    handleLabelClick:
      ({ inputRef }) =>
      () =>
        inputRef && inputRef.click()
  })
)(ToggleSwitch)
